import { useState } from 'react'
import styles from './ConfirmationModal.module.css'
import useEscapeKey from '../../hooks/useEscapeKey'

/**
 * Reusable confirmation modal component
 * Follows Single Responsibility Principle - only handles confirmation dialogs
 * Follows Open-Closed Principle - extensible through props without modification
 */
export default function ConfirmationModal({ 
    isOpen, 
    onClose, 
    onConfirm,
    title = "Confirm Action",
    message = "Are you sure you want to proceed?",
    confirmText = "Confirm",
    cancelText = "Cancel",
    confirmButtonStyle = "danger", // 'primary', 'danger', 'warning'
    isLoading = false
}) {
    const [confirming, setConfirming] = useState(false)

    // Handle escape key to close modal (disabled when confirming or loading)
    useEscapeKey(isOpen, onClose, !confirming && !isLoading)

    const handleConfirm = async () => {
        if (confirming || isLoading) return
        
        setConfirming(true)
        try {
            await onConfirm()
        } finally {
            setConfirming(false)
        }
    }

    const handleCancel = () => {
        if (confirming || isLoading) return
        onClose()
    }

    if (!isOpen) return null

    const confirmButtonClass = {
        primary: styles.confirmButtonPrimary,
        danger: styles.confirmButtonDanger,
        warning: styles.confirmButtonWarning
    }[confirmButtonStyle] || styles.confirmButtonDanger

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>{title}</h2>
                    <button 
                        className={styles.closeButton}
                        onClick={handleCancel}
                        aria-label="Close modal"
                        disabled={confirming || isLoading}
                    >
                        ×
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <p>{message}</p>
                </div>

                <div className={styles.modalFooter}>
                    <button
                        type="button"
                        className={styles.cancelButton}
                        onClick={handleCancel}
                        disabled={confirming || isLoading}
                    >
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        className={`${styles.confirmButton} ${confirmButtonClass}`}
                        onClick={handleConfirm}
                        disabled={confirming || isLoading}
                    >
                        {confirming || isLoading ? 'Processing...' : confirmText}
                    </button>
                </div>
            </div>
        </div>
    )
}